"use client";

import React, { useState, useRef, useCallback } from "react";
import {
  Text,
  Section,
  SectionCard,
  CardTitle,
  CardDescription,
  Button,
  Badge,
  KPICard,
} from "@/src/components";
import { cn } from "@/src/utils/utils";
import { useScrollReveal, useCountUp, staggeredEntryClasses } from "@/src/utils/motion-utils";
import { Play, RotateCcw, Zap, Eye, Shield, Layers } from "lucide-react";

// ---------------------------------------------------------------------------
// Reusable demo wrapper
// ---------------------------------------------------------------------------

function DemoBox({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "relative rounded-lg border border-dashed border-border bg-muted/30 p-6 overflow-hidden",
        className
      )}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Principles
// ---------------------------------------------------------------------------

const principles = [
  {
    icon: <Zap className="h-5 w-5" />,
    title: "Purposeful",
    description:
      "Every animation serves a functional purpose — guiding attention, providing feedback, or communicating state changes.",
  },
  {
    icon: <Play className="h-5 w-5" />,
    title: "Responsive",
    description:
      "Asymmetric timing: elements enter at 250ms and exit at 200ms. Faster exits feel more responsive.",
  },
  {
    icon: <Shield className="h-5 w-5" />,
    title: "Accessible",
    description:
      "All animations respect prefers-reduced-motion. Users who opt out get instant state changes with zero motion.",
  },
  {
    icon: <Layers className="h-5 w-5" />,
    title: "CSS-First",
    description:
      "Pure CSS animations and transitions. No runtime animation libraries. Keeps the bundle lean and performance high.",
  },
];

// ---------------------------------------------------------------------------
// Token data
// ---------------------------------------------------------------------------

const durationTokens = [
  { name: "fast", token: "--duration-fast", value: "100ms", use: "Hover, focus, micro-interactions" },
  { name: "normal", token: "--duration-normal", value: "200ms", use: "Dropdowns, tabs, standard transitions" },
  { name: "slow", token: "--duration-slow", value: "300ms", use: "Modals, page transitions" },
  { name: "enter", token: "--duration-enter", value: "250ms", use: "Content entering the viewport" },
  { name: "exit", token: "--duration-exit", value: "200ms", use: "Content leaving the viewport" },
];

const easingTokens = [
  { name: "default", token: "--ease-default", value: "cubic-bezier(0.4, 0, 0.2, 1)", use: "General purpose" },
  { name: "in", token: "--ease-in", value: "cubic-bezier(0.4, 0, 1, 1)", use: "Elements accelerating out" },
  { name: "out", token: "--ease-out", value: "cubic-bezier(0, 0, 0.2, 1)", use: "Elements decelerating in" },
  { name: "bounce", token: "--ease-bounce", value: "cubic-bezier(0.34, 1.56, 0.64, 1)", use: "Playful overshoot" },
  { name: "spring", token: "--ease-spring", value: "cubic-bezier(0.175, 0.885, 0.32, 1.275)", use: "Modal/dialog entry" },
  { name: "smooth", token: "--ease-smooth", value: "cubic-bezier(0.45, 0, 0.55, 1)", use: "Content fades, subtle motion" },
];

// ---------------------------------------------------------------------------
// Keyframe animation catalog
// ---------------------------------------------------------------------------

const keyframeAnimations = [
  { name: "fade-in-up", className: "animate-fade-in-up", description: "Content entry from below" },
  { name: "fade-in-down", className: "animate-fade-in-down", description: "Dropdown/menu entry" },
  { name: "scale-in", className: "animate-scale-in", description: "Modal/dialog entry" },
  { name: "scale-out", className: "animate-scale-out", description: "Modal/dialog exit" },
  { name: "slide-in-right", className: "animate-slide-in-right", description: "Side panel entry" },
  { name: "slide-out-right", className: "animate-slide-out-right", description: "Side panel exit" },
  { name: "fade-in", className: "animate-fade-in", description: "Simple opacity fade in" },
  { name: "fade-out", className: "animate-fade-out", description: "Simple opacity fade out" },
  { name: "progress-fill", className: "animate-progress-fill", description: "Progress bar fill" },
];

// ---------------------------------------------------------------------------
// Interactive duration demo
// ---------------------------------------------------------------------------

function DurationPreview({ token, value }: { token: string; value: string }) {
  const [moved, setMoved] = useState(false);
  return (
    <button
      type="button"
      className="relative h-8 flex-1 rounded-md bg-muted/60 overflow-hidden cursor-pointer"
      onClick={() => setMoved((m) => !m)}
    >
      <div
        className={cn(
          "absolute top-1 bottom-1 left-1 w-8 rounded bg-primary transition-all",
          moved && "left-[calc(100%-2.25rem)]"
        )}
        style={{ transitionDuration: value }}
      />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Easing curve demo
// ---------------------------------------------------------------------------

function EasingPreview({ value }: { value: string }) {
  const [moved, setMoved] = useState(false);
  return (
    <button
      type="button"
      className="relative h-8 flex-1 rounded-md bg-muted/60 overflow-hidden cursor-pointer"
      onClick={() => setMoved((m) => !m)}
    >
      <div
        className={cn(
          "absolute top-1 bottom-1 left-1 w-8 rounded bg-primary transition-all duration-500",
        )}
        style={{
          transitionTimingFunction: value,
          transform: moved ? "translateX(calc(100% + 3rem))" : "translateX(0)",
        }}
      />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Keyframe animation demo cell
// ---------------------------------------------------------------------------

function KeyframeDemoCell({
  name,
  className: animClass,
  description,
}: {
  name: string;
  className: string;
  description: string;
}) {
  const [key, setKey] = useState(0);
  const replay = () => setKey((k) => k + 1);

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border p-4">
      <div className="flex items-center justify-between">
        <Text className="font-mono text-xs">{name}</Text>
        <Button variant="ghost" size="sm" onClick={replay}>
          <RotateCcw className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="flex items-center justify-center h-20 bg-muted/30 rounded-md overflow-hidden">
        <div
          key={key}
          className={cn("h-12 w-12 rounded-lg bg-primary", animClass)}
        />
      </div>
      <Text className="text-xs text-muted-foreground">{description}</Text>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stagger demo
// ---------------------------------------------------------------------------

function StaggerDemo() {
  const [key, setKey] = useState(0);
  const items = ["Revenue", "Expenses", "Profit", "Cash Flow", "AR", "AP"];

  return (
    <div className="space-y-4">
      <Button variant="outline" size="sm" onClick={() => setKey((k) => k + 1)}>
        <RotateCcw className="h-3.5 w-3.5 mr-2" />
        Replay Stagger
      </Button>
      <div key={key} className="stagger-children grid grid-cols-2 md:grid-cols-3 gap-3">
        {items.map((item, i) => (
          <div
            key={item}
            className="rounded-lg bg-primary/10 border border-primary/20 p-4 text-center"
          >
            <Text className="font-semibold text-sm">{item}</Text>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scroll reveal demo
// ---------------------------------------------------------------------------

function ScrollRevealDemo() {
  const items = Array.from({ length: 8 }, (_, i) => `Item ${i + 1}`);

  return (
    <div className="max-h-[300px] overflow-y-auto rounded-lg border border-border p-4 space-y-3">
      <Text className="text-xs text-muted-foreground sticky top-0 bg-background py-1 z-10">
        ↓ Scroll down to see items reveal
      </Text>
      {items.map((item, i) => (
        <ScrollRevealItem key={item} label={item} />
      ))}
    </div>
  );
}

function ScrollRevealItem({ label }: { label: string }) {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>({ threshold: 0.3 });

  return (
    <div
      ref={ref}
      className={cn(
        "rounded-lg bg-accent/50 p-4 transition-all duration-300",
        isVisible ? "animate-fade-in-up opacity-100" : "opacity-0 translate-y-2"
      )}
    >
      <Text className="font-medium text-sm">{label}</Text>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Count-up demo
// ---------------------------------------------------------------------------

function CountUpDemo() {
  const [key, setKey] = useState(0);

  return (
    <div className="space-y-4">
      <Button variant="outline" size="sm" onClick={() => setKey((k) => k + 1)}>
        <RotateCcw className="h-3.5 w-3.5 mr-2" />
        Replay Count
      </Button>
      <div key={key} className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <CountUpCard label="Revenue" target={2847500} prefix="$" suffix="" decimals={0} />
        <CountUpCard label="Users" target={12483} prefix="" suffix="" decimals={0} />
        <CountUpCard label="Growth" target={23.5} prefix="" suffix="%" decimals={1} />
        <CountUpCard label="Close Rate" target={87} prefix="" suffix="%" decimals={0} />
      </div>
    </div>
  );
}

function CountUpCard({
  label,
  target,
  prefix,
  suffix,
  decimals,
}: {
  label: string;
  target: number;
  prefix: string;
  suffix: string;
  decimals: number;
}) {
  const display = useCountUp(target, { duration: 1200, decimals, prefix, suffix });

  return (
    <div className="rounded-lg border border-border p-4 text-center">
      <Text className="text-2xl font-bold tabular-nums">{display}</Text>
      <Text className="text-xs text-muted-foreground mt-1">{label}</Text>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Motion System Page
// ---------------------------------------------------------------------------

export default function MotionSystemPage() {
  return (
    <div className="space-y-theme-5 overflow-auto h-full py-theme-6 px-theme-6">
      {/* ─── Header ─── */}
      <Section>
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <Text as="h1" className="text-2xl font-bold tracking-tight">
              Motion System
            </Text>
            <Badge variant="outline">CSS-First</Badge>
          </div>
          <Text className="text-base text-muted-foreground max-w-3xl">
            A complete animation system built entirely with CSS keyframes and custom properties.
            Zero runtime dependencies, full reduced-motion support, and seamless dark mode transitions.
          </Text>
        </div>
      </Section>

      {/* ─── Principles ─── */}
      <Section title="Design Principles">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {principles.map((p) => (
            <SectionCard
              key={p.title}
              header={
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10 text-primary">{p.icon}</div>
                  <CardTitle className="text-sm">{p.title}</CardTitle>
                </div>
              }
              contentClassName="p-theme-4"
            >
              <Text className="text-xs text-muted-foreground leading-relaxed">
                {p.description}
              </Text>
            </SectionCard>
          ))}
        </div>
      </Section>

      {/* ─── Duration Tokens ─── */}
      <Section title="Duration Tokens">
        <SectionCard
          header={
            <>
              <CardTitle>Timing Scale</CardTitle>
              <CardDescription>Click each row to see the duration in action</CardDescription>
            </>
          }
          contentClassName="p-theme-5"
        >
          <div className="space-y-3">
            {durationTokens.map((d) => (
              <div key={d.token} className="flex items-center gap-4">
                <div className="w-16">
                  <Text className="font-semibold text-sm">{d.name}</Text>
                </div>
                <code className="text-xs bg-muted px-2 py-1 rounded w-20 text-center">
                  {d.value}
                </code>
                <DurationPreview token={d.token} value={d.value} />
                <Text className="text-xs text-muted-foreground w-48 hidden lg:block">{d.use}</Text>
              </div>
            ))}
          </div>
        </SectionCard>
      </Section>

      {/* ─── Easing Tokens ─── */}
      <Section title="Easing Functions">
        <SectionCard
          header={
            <>
              <CardTitle>Timing Functions</CardTitle>
              <CardDescription>Click each row to compare easing curves</CardDescription>
            </>
          }
          contentClassName="p-theme-5"
        >
          <div className="space-y-3">
            {easingTokens.map((e) => (
              <div key={e.token} className="flex items-center gap-4">
                <div className="w-16">
                  <Text className="font-semibold text-sm">{e.name}</Text>
                </div>
                <EasingPreview value={e.value} />
                <Text className="text-xs text-muted-foreground w-48 hidden lg:block">{e.use}</Text>
              </div>
            ))}
          </div>
        </SectionCard>
      </Section>

      {/* ─── Keyframe Catalog ─── */}
      <Section title="Keyframe Animations">
        <SectionCard
          header={
            <>
              <CardTitle>Animation Catalog</CardTitle>
              <CardDescription>
                Click the replay button on each card to see the animation
              </CardDescription>
            </>
          }
          contentClassName="p-theme-5"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {keyframeAnimations.map((anim) => (
              <KeyframeDemoCell key={anim.name} {...anim} />
            ))}
          </div>
        </SectionCard>
      </Section>

      {/* ─── Stagger Pattern ─── */}
      <Section title="Stagger Pattern">
        <SectionCard
          header={
            <>
              <CardTitle>Staggered Children</CardTitle>
              <CardDescription>
                Apply <code className="bg-muted px-1 rounded text-xs">.stagger-children</code> to
                a parent. Each child fades in with a 50ms stagger delay.
              </CardDescription>
            </>
          }
          contentClassName="p-theme-5"
        >
          <StaggerDemo />
          <div className="mt-4 bg-muted/50 rounded-lg p-4 font-mono text-xs space-y-1">
            <code className="block text-muted-foreground">{"// HTML"}</code>
            <code className="block">{'<div className="stagger-children">'}</code>
            <code className="block pl-4">{"<Card>Revenue</Card>"}</code>
            <code className="block pl-4">{"<Card>Expenses</Card>"}</code>
            <code className="block pl-4">{"<Card>Profit</Card>"}</code>
            <code className="block">{"</div>"}</code>
          </div>
        </SectionCard>
      </Section>

      {/* ─── Scroll Reveal ─── */}
      <Section title="Scroll Reveal">
        <SectionCard
          header={
            <>
              <CardTitle>useScrollReveal Hook</CardTitle>
              <CardDescription>
                Uses IntersectionObserver to trigger animations when elements enter the viewport.
                Fires once by default.
              </CardDescription>
            </>
          }
          contentClassName="p-theme-5"
        >
          <ScrollRevealDemo />
          <div className="mt-4 bg-muted/50 rounded-lg p-4 font-mono text-xs space-y-1">
            <code className="block text-muted-foreground">{"// React hook"}</code>
            <code className="block">{"const { ref, isVisible } = useScrollReveal();"}</code>
            <code className="block">&nbsp;</code>
            <code className="block">{"<div ref={ref}"}</code>
            <code className="block pl-4">{"className={isVisible ? 'animate-fade-in-up' : 'opacity-0'}"}</code>
            <code className="block">{"/>"}</code>
          </div>
        </SectionCard>
      </Section>

      {/* ─── Count Up ─── */}
      <Section title="Count Up">
        <SectionCard
          header={
            <>
              <CardTitle>useCountUp Hook</CardTitle>
              <CardDescription>
                Animates numbers from 0 to target using requestAnimationFrame with ease-out-cubic
                easing. Supports prefix, suffix, and decimal control.
              </CardDescription>
            </>
          }
          contentClassName="p-theme-5"
        >
          <CountUpDemo />
          <div className="mt-4 bg-muted/50 rounded-lg p-4 font-mono text-xs space-y-1">
            <code className="block text-muted-foreground">{"// React hook"}</code>
            <code className="block">
              {'const value = useCountUp(2847500, {'}
            </code>
            <code className="block pl-4">{"duration: 1200,"}</code>
            <code className="block pl-4">{'prefix: "$",'}</code>
            <code className="block pl-4">{"decimals: 0,"}</code>
            <code className="block">{"});"}</code>
          </div>
        </SectionCard>
      </Section>

      {/* ─── Accessibility ─── */}
      <Section title="Accessibility">
        <SectionCard
          header={
            <>
              <CardTitle>Reduced Motion Support</CardTitle>
              <CardDescription>
                All animations automatically respect the user's OS preference.
              </CardDescription>
            </>
          }
          contentClassName="p-theme-5 space-y-4"
        >
          <Text className="text-sm text-muted-foreground">
            When <code className="mx-1 bg-muted px-1 rounded">prefers-reduced-motion: reduce</code> is
            active, all CSS keyframe animations are disabled and elements appear instantly. The
            <code className="mx-1 bg-muted px-1 rounded">useScrollReveal</code> and
            <code className="mx-1 bg-muted px-1 rounded">useCountUp</code> hooks also check this
            preference and skip animation.
          </Text>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-xs space-y-1">
            <code className="block">{"@media (prefers-reduced-motion: reduce) {"}</code>
            <code className="block pl-4">{".animate-fade-in-up,"}</code>
            <code className="block pl-4">{".animate-scale-in,"}</code>
            <code className="block pl-4">{".stagger-children > * {"}</code>
            <code className="block pl-8">{"animation: none !important;"}</code>
            <code className="block pl-8">{"opacity: 1 !important;"}</code>
            <code className="block pl-8">{"transform: none !important;"}</code>
            <code className="block pl-4">{"}"}</code>
            <code className="block">{"}"}</code>
          </div>
        </SectionCard>
      </Section>

      {/* ─── Usage Guide ─── */}
      <Section title="Quick Reference">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SectionCard
            header={<CardTitle className="text-sm">CSS Classes</CardTitle>}
            contentClassName="p-theme-4"
          >
            <div className="space-y-2 text-xs font-mono">
              <div className="flex justify-between"><span>.animate-fade-in-up</span><span className="text-muted-foreground">Content entry</span></div>
              <div className="flex justify-between"><span>.animate-fade-in-down</span><span className="text-muted-foreground">Dropdown entry</span></div>
              <div className="flex justify-between"><span>.animate-scale-in</span><span className="text-muted-foreground">Modal entry</span></div>
              <div className="flex justify-between"><span>.animate-scale-out</span><span className="text-muted-foreground">Modal exit</span></div>
              <div className="flex justify-between"><span>.animate-slide-in-right</span><span className="text-muted-foreground">Panel entry</span></div>
              <div className="flex justify-between"><span>.animate-slide-out-right</span><span className="text-muted-foreground">Panel exit</span></div>
              <div className="flex justify-between"><span>.animate-progress-fill</span><span className="text-muted-foreground">Progress bars</span></div>
              <div className="flex justify-between"><span>.animate-check-stroke</span><span className="text-muted-foreground">Checkboxes</span></div>
              <div className="flex justify-between"><span>.stagger-children</span><span className="text-muted-foreground">Parent wrapper</span></div>
            </div>
          </SectionCard>

          <SectionCard
            header={<CardTitle className="text-sm">React Hooks</CardTitle>}
            contentClassName="p-theme-4"
          >
            <div className="space-y-3 text-xs">
              <div>
                <code className="font-mono bg-muted px-1 rounded">useScrollReveal()</code>
                <Text className="text-muted-foreground mt-1">
                  Returns ref and isVisible. Attach ref to element, toggle animation class based on isVisible.
                </Text>
              </div>
              <div>
                <code className="font-mono bg-muted px-1 rounded">useCountUp(target, options)</code>
                <Text className="text-muted-foreground mt-1">
                  Returns formatted string. Options: duration, decimals, prefix, suffix, enabled.
                </Text>
              </div>
              <div>
                <code className="font-mono bg-muted px-1 rounded">staggeredEntryClasses(index)</code>
                <Text className="text-muted-foreground mt-1">
                  Returns CSSProperties with animation-delay for JS-driven stagger.
                </Text>
              </div>
            </div>
          </SectionCard>
        </div>
      </Section>
    </div>
  );
}

export { MotionSystemPage };
